import { useState, type ReactElement } from 'react';
import { Alert, Button, Upload, Tooltip } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityRelatedList } from '../../../components/entity-detail/EntityRelatedList.js';
import type { EntityRelatedContext } from '../../../components/entity-detail/related-context.js';
import { formatDateTime } from '../../../shared/format/entity-detail.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import {
  deleteAttachment,
  getDownloadUrl,
  uploadAttachment,
} from '../../attachments/api/attachments.client.js';
import { useEntityFiles } from '../hooks/useContactFiles.js';

export function ContactFilesTab({ contactId }: { contactId: string }): ReactElement {
  return <EntityFilesTab contactId={contactId} />;
}

export function EntityFilesTab({
  contactId,
  dealId,
  companyId,
  taskId,
}: EntityRelatedContext): ReactElement {
  const { t, locale } = useTranslation();
  const scope = useCurrentScope();
  const entity = taskId ? 'task' : dealId ? 'deal' : companyId ? 'company' : 'contact';
  const entityId = taskId ?? dealId ?? companyId ?? contactId!;
  const query = useEntityFiles(entity, entityId);
  const cache = useQueryClient();
  const [openError, setOpenError] = useState(false);
  const invalidate = async (): Promise<void> => {
    await Promise.all(
      ['attachments', 'activities'].map((root) =>
        cache.invalidateQueries({ queryKey: [root, scope?.workspaceId ?? 'anonymous'] }),
      ),
    );
  };
  const upload = useMutation({
    mutationFn: (file: File) => uploadAttachment(entity, entityId, file),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: deleteAttachment, onSuccess: invalidate });
  const download = async (id: string): Promise<void> => {
    setOpenError(false);
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const url = await getDownloadUrl(id);
      if (tab) tab.location.replace(url);
      else window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      tab?.close();
      setOpenError(true);
    }
  };
  return (
    <>
      {upload.error || remove.error || openError ? (
        <Alert type="error" showIcon title={t('crm.entity_detail.save_error')} />
      ) : null}
      <EntityRelatedList
        loading={query.isLoading}
        error={query.error}
        emptyText={t('crm.entity_detail.empty_files')}
        action={
          <Upload
            showUploadList={false}
            beforeUpload={(file) => {
              upload.mutate(file);
              return false;
            }}
          >
            <Button size="small" icon={<UploadOutlined />} loading={upload.isPending}>
              {t('crm.entity_detail.add_file')}
            </Button>
          </Upload>
        }
        rows={(query.data ?? []).map((file) => ({
          id: file.id,
          title: file.originalName,
          onOpen: () => void download(file.id),
          meta: `${new Intl.NumberFormat(locale, { style: 'unit', unit: file.size < 1024 * 1024 ? 'kilobyte' : 'megabyte', maximumFractionDigits: 1 }).format(file.size / (file.size < 1024 * 1024 ? 1024 : 1024 * 1024))} · ${formatDateTime(file.createdAt)}`,
          visibleActions: (
            <Tooltip title={t('crm.entity_detail.download')}>
              <Button
                type="text"
                size="small"
                icon={<DownloadOutlined />}
                aria-label={t('crm.entity_detail.download')}
                onClick={() => void download(file.id)}
              />
            </Tooltip>
          ),
          actions: [
            {
              key: 'delete',
              label: t('crm.entity_detail.delete'),
              danger: true,
              disabled: remove.isPending,
              confirm: { title: t('crm.entity_detail.delete_confirm') },
              onClick: () => remove.mutateAsync(file.id),
            },
          ],
        }))}
      />
    </>
  );
}
